import { create } from "zustand";

import { AuthManager } from "@v2/auth/client";
import type { AuthUser } from "@/lib/(v2)/auth/types";

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  syncFromManager: () => void;
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  loading: true,

  syncFromManager: () => {
    const auth = AuthManager.getInstance();
    set({
      user: auth.getUser(),
      isAuthenticated: auth.isAuthenticated(),
      loading: false,
    });
  },

  initialize: async () => {
    const auth = AuthManager.getInstance();
    if (auth.isAuthenticated()) {
      set({
        user: auth.getUser(),
        isAuthenticated: true,
        loading: false,
      });
      return;
    }

    if (auth.hasSession()) {
      const refreshed = await auth.refreshAccessToken();
      if (refreshed) {
        set({
          user: auth.getUser(),
          isAuthenticated: true,
          loading: false,
        });
        return;
      }
    }

    set({ user: null, isAuthenticated: false, loading: false });
  },

  login: async (email, password) => {
    const auth = AuthManager.getInstance();
    await auth.login(email, password);
    set({
      user: auth.getUser(),
      isAuthenticated: true,
      loading: false,
    });
  },

  logout: async () => {
    await AuthManager.getInstance().logout();
    set({ user: null, isAuthenticated: false, loading: false });
  },
}));
